import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import ColorFilter from '../../../Component/ColorFilter';
import Point from '../../../Utility/Point';
import CardSystem from '../../Card/CardSystem';
import Container from '../../../Prototype/Container';
import { StartAnimation } from '../AnimationHelper';
import Renderable from '../../../Component/Renderable';
import { CARD_STACK_OFFSET } from '../../../Constants/InGame';
import CardComponent from 'Component/Level/Card';

interface ICardPlusThreeMeta {
    system: CardSystem;
    sourceCard: BaseCard;
    cardsContainer: Container;
    stackContainer: GameObject;
}

export class HelperPlusCards extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta: ICardPlusThreeMeta
    ): IterableIterator<number> {
        const gameObjectTransform: TransformComponent = gameObject.getComponent(
            TransformComponent
        );

        const basePoint: Point = new Point(
            gameObjectTransform.position.x,
            gameObjectTransform.position.y - 150
        );

        const plusThreeEffect = GameObject.Instantiate(Container);
        meta.sourceCard.addChild(plusThreeEffect);
        this.gameObject.addChild(meta.sourceCard);
        this.gameObject.addChild(plusThreeEffect);

        StartAnimation(plusThreeEffect, 'BoomExplosionEffect');

        const targetCards: GameObject[] = meta.system.store
            .getState().levelState.cardsState.inStack
            .slice(-3)
            .map((cardID) => meta.system.getCardByID(cardID));

        targetCards.forEach((card) => {
            card.getComponent(Renderable).visible = false
            card.getComponent(CardComponent).skipStackAlign = true;
        });

        yield* delay(500);

        const newCardPositions = [
            { x: -125, y: -50, rotation: -Math.PI * 0.15 },
            { x: 0, y: -130, rotation: 0 },
            { x: 125, y: -50, rotation: Math.PI * 0.15 },
        ];

        const cardOutDuration = 300;
        const cardInDuration = 600;

        for (let i = 0; i < newCardPositions.length; i++) {
            const newCard = targetCards[i];
            const cardComponent = newCard.getComponent(CardComponent);
            newCard.getComponent(Renderable).visible = true;
            newCard.addComponent(ColorFilter, {
                brightness: 10,
            });

            // Add as child to animator first for relative positioning.
            gameObject.addChild(newCard);

            const newCardTransform = newCard.getComponent(TransformComponent);
            newCardTransform.position.copy(basePoint as Point);

            // Add as child to stack container.

            meta.stackContainer.addChild(newCard);

            const tweenPosition = this.getTween(newCardTransform.position);
            const tweenRotation = this.getTween(newCardTransform);

            tweenPosition
                .to(
                    {
                        x: newCardTransform.position.x + newCardPositions[i].x,
                        y: newCardTransform.position.y + newCardPositions[i].y,
                    },
                    cardOutDuration
                )
                .easing(Easing.Cubic.Out)
                .start(this.elapsedTime);

            tweenRotation
                .to(
                    {
                        rotation: newCardPositions[i].rotation,
                    },
                    cardOutDuration
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            const tweenBrightness = this.getTween(
                newCard.getComponent(ColorFilter)
            );
            tweenBrightness
                .to(
                    {
                        brightness: 1,
                    },
                    cardOutDuration + 100
                )
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);
        }

        yield* delay(cardOutDuration);

        for (let i = 0; i < newCardPositions.length; i++) {
            const newCard = targetCards[i];
            const newCardTransform = newCard.getComponent(TransformComponent);

            const tweenPosition = this.getTween(newCardTransform.position);
            const tweenRotation = this.getTween(newCardTransform);

            tweenPosition
                .to(
                    {
                        x: CARD_STACK_OFFSET,
                        y: 0,
                    },
                    cardInDuration
                )
                .delay(200 + i * 100)
                .onComplete(() => {
                    newCard.getComponent(CardComponent).skipStackAlign = false;
                    meta.system.cardStack.align();
                })
                .start(this.elapsedTime);

            tweenRotation
                .to(
                    {
                        rotation: 0,
                    },
                    cardInDuration
                )
                .delay(200 + i * 100)
                .start(this.elapsedTime);
        }

        yield* delay(cardInDuration + 500);
    }
}
